import { formatMatchScore } from "../../../../helpers/matchScore";

const getUniqueArrayObjectsByKey = (array, key) =>
  Array.from(
    array.reduce((map, current) => {
      if (!map.has(current[key])) {
        map.set(current[key], current);
      }
      return map;
    }, new Map()).values()
  );

const toNumericId = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const MATCH_DATE_KEYS = [
  "date",
  "match_date",
  "matchDate",
  "kickoff_at",
  "kickoffAt",
  "start_time",
  "started_at",
  "datetime",
];

const getMatchTimestamp = (match) => {
  if (!match || typeof match !== "object") {
    return null;
  }

  for (const key of MATCH_DATE_KEYS) {
    const rawDate = match?.[key];
    if (!rawDate) {
      continue;
    }

    const parsedDate = new Date(rawDate).getTime();
    if (!Number.isNaN(parsedDate)) {
      return parsedDate;
    }
  }

  return null;
};

const hasRecordedScore = (match) => {
  const homeScore = Number(match?.home_team_score);
  const awayScore = Number(match?.away_team_score);

  if (Number.isFinite(homeScore) && Number.isFinite(awayScore)) {
    return true;
  }

  if (typeof match?.score === "string") {
    return /\d/.test(match.score);
  }

  return false;
};

const isPlayedMatch = (match) => {
  const matchTimestamp = getMatchTimestamp(match);

  if (matchTimestamp === null) {
    return hasRecordedScore(match);
  }

  return matchTimestamp <= Date.now();
};

const toUtcDayKeyTimestamp = (value) => {
  const timestamp = new Date(value).getTime();
  if (!Number.isFinite(timestamp)) {
    return null;
  }

  const date = new Date(timestamp);
  return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
};

export const getSeasonSummaryLabel = (selectedSeason = [], fallbackLabel = "") => {
  if (selectedSeason.length > 0) {
    const years = selectedSeason.flatMap((item) => item?.years).filter(Boolean);
    if (years.length > 2) {
      return `${years[0]}, ${years[1]} + ${years.length - 2}`;
    }
    if (years.length > 0) {
      return years.join(", ");
    }
  }

  return fallbackLabel;
};

export const getCompetitionSummaryLabel = (
  selectedCompetition = [],
  fallbackLabel = ""
) => {
  if (selectedCompetition.length > 0) {
    const competitions = selectedCompetition
      .flatMap((item) => item?.competition)
      .filter(Boolean);
    if (competitions.length > 1) {
      return `${competitions[0]} + ${competitions.length - 1}`;
    }
    if (competitions.length > 0) {
      return competitions.join(", ");
    }
  }

  return fallbackLabel;
};

export const getMatchesSummaryLabel = (selectedMatches = [], fallbackLabel = "") => {
  if (selectedMatches.length > 0) {
    const labels = selectedMatches
      .map(
        (item) =>
          `${item?.home_team || ""} ${formatMatchScore(item)} ${item?.away_team || ""} ${
            item?.competition || ""
          }`.trim()
      )
      .filter(Boolean);

    if (labels.length > 1) {
      return `${labels[0]} + ${labels.length - 1}`;
    }

    if (labels.length === 1) {
      return labels[0];
    }
  }

  return fallbackLabel;
};

export const createMatchesCountOptions = (matchesLength, l) => [
  { id: 1, value: matchesLength,  title: l(1066) },
  { id: 6, value: 1,              title: l(1688) || "Last Match" },
  { id: 2, value: 3,              title: l(1067) },
  { id: 3, value: 5,              title: l(1068) },
  { id: 4, value: 10,             title: l(1069) },
];

export const deriveCompetitionsFromMatches = (matches = []) => {
  if (!Array.isArray(matches) || matches.length === 0) {
    return [];
  }

  return getUniqueArrayObjectsByKey(matches, "competition_id");
};

export const reconcileCompetitionSelection = ({
  availableCompetitions = [],
  selectedCompetitions = [],
  hasCustomSelection = false,
}) => {
  if (!Array.isArray(availableCompetitions) || availableCompetitions.length === 0) {
    return [];
  }

  if (!hasCustomSelection) {
    return availableCompetitions;
  }

  if (!Array.isArray(selectedCompetitions)) {
    return availableCompetitions;
  }

  const availableCompetitionIds = new Set(
    availableCompetitions
      .map((item) => toNumericId(item?.competition_id))
      .filter((id) => id !== null)
  );

  return selectedCompetitions.filter((item) =>
    availableCompetitionIds.has(toNumericId(item?.competition_id))
  );
};

export const deriveSelectedMatches = ({
  matches = [],
  selectedCompetition = [],
  selectedMatchCount = null,
}) => {
  if (!Array.isArray(matches) || matches.length === 0) {
    return [];
  }

  const selectedCompetitionIds = selectedCompetition.map((item) =>
    Number(item?.competition_id)
  );

  const competitionFilteredMatches =
    selectedCompetitionIds.length > 0
      ? matches.filter((match) =>
          selectedCompetitionIds.includes(Number(match?.competition_id))
        )
      : [];

  if (!selectedMatchCount) {
    return competitionFilteredMatches;
  }

  if (selectedMatchCount.value === 0) {
    return [];
  }

  const playedCompetitionMatches = competitionFilteredMatches.filter(isPlayedMatch);
  const isAllMatchesOption = Number(selectedMatchCount?.id) === 1;
  if (isAllMatchesOption) {
    return playedCompetitionMatches;
  }

  const presetMatchesPool =
    playedCompetitionMatches.length > 0
      ? playedCompetitionMatches
      : competitionFilteredMatches;

  return presetMatchesPool.slice(0, selectedMatchCount.value);
};

export const getSelectedMatchIds = (selectedMatches = []) =>
  selectedMatches.flatMap((item) => item?.id).filter(Boolean);

export const filterMatchesByDateRange = ({
  matches = [],
  startDate,
  endDate,
}) => {
  const startDayTimestamp = toUtcDayKeyTimestamp(startDate);
  const endDayTimestamp = toUtcDayKeyTimestamp(endDate);

  if (startDayTimestamp === null || endDayTimestamp === null) {
    return [];
  }

  return (matches || []).filter((match) => {
    if (!match?.date) {
      return false;
    }

    const matchDayTimestamp = toUtcDayKeyTimestamp(match.date);
    if (matchDayTimestamp === null) {
      return false;
    }

    return (
      matchDayTimestamp >= startDayTimestamp &&
      matchDayTimestamp <= endDayTimestamp
    );
  });
};

export const applyTemplateParameterIdsToGroups = (
  parameterGroups = [],
  parameterIds = []
) => {
  if (!Array.isArray(parameterGroups) || parameterGroups.length === 0) {
    return {
      nextParameterGroups: [],
      selectedParameters: {
        checkedIds: [],
        params: [],
      },
    };
  }

  const normalizedParameterIds = Array.isArray(parameterIds)
    ? parameterIds
        .map(toNumericId)
        .filter((id) => id !== null)
    : [];
  const parameterIdSet = new Set(normalizedParameterIds);

  const nextParameterGroups = parameterGroups.map((group) => {
    const groupName = group?.[0];
    const groupParameters = Array.isArray(group?.[1]) ? group[1] : [];

    return [
      groupName,
      groupParameters.map((parameter) => ({
        ...parameter,
        checked: parameterIdSet.has(Number(parameter?.id)),
      })),
    ];
  });

  const checkedParams = nextParameterGroups.flatMap((group) =>
    group[1].filter((parameter) => parameter?.checked)
  );

  return {
    nextParameterGroups,
    selectedParameters: {
      checkedIds: checkedParams.map((parameter) => parameter.id),
      params: checkedParams,
    },
  };
};

export const normalizeMarkerCollection = (markers) => {
  if (typeof markers === "string") {
    return markers
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }

  if (!Array.isArray(markers)) {
    return [];
  }

  return markers
    .map((item) => {
      if (item && typeof item === "object") {
        return item.id ?? null;
      }
      return item;
    })
    .filter((item) => item !== null && item !== undefined && item !== "");
};

export const aggregateStatsResponse = (stats = []) => {
  const aggregatedMap = new Map();

  (stats || []).forEach((stat) => {
    (stat?.parameters || []).forEach((parameter) => {
      const parameterId = Number(
        parameter?.parameter_id ?? parameter?.id ?? parameter?.param_id
      );
      const parameterName =
        parameter?.parameter_name || parameter?.name || parameter?.title || "";

      if (!parameterId && !parameterName) {
        return;
      }

      const mapKey = `${parameterId || parameterName}:${parameterName}`;
      const previousValue = aggregatedMap.get(mapKey) || {
        id: parameterId,
        param_id: parameterId,
        parameter_id: parameterId,
        name: parameterName,
        parameter_name: parameterName,
        count: 0,
        value: 0,
        markers: [],
      };

      const nextValue = Number(parameter?.value ?? parameter?.count ?? 0);
      const safeValue = Number.isFinite(nextValue) ? nextValue : 0;
      const hasUniqueCount =
        parameter?.event_count !== undefined ||
        parameter?.unique_markers_count !== undefined ||
        parameter?.unique_count !== undefined;
      const nextUniqueCount = Number(
        parameter?.event_count ?? parameter?.unique_markers_count ?? parameter?.unique_count
      );
      const safeUniqueCount = Number.isFinite(nextUniqueCount)
        ? nextUniqueCount
        : 0;

      previousValue.count += safeValue;
      previousValue.value = previousValue.count;
      if (hasUniqueCount) {
        previousValue.event_count =
          (previousValue.event_count || 0) + safeUniqueCount;
        previousValue.unique_count = previousValue.event_count;
        previousValue.unique_markers_count = previousValue.event_count;
      }
      previousValue.markers = [
        ...previousValue.markers,
        ...normalizeMarkerCollection(parameter?.markers),
      ];

      aggregatedMap.set(mapKey, previousValue);
    });
  });

  return Array.from(aggregatedMap.values()).sort(
    (a, b) => (a.parameter_id || 0) - (b.parameter_id || 0)
  );
};

const getStatIdentity = (stat) => {
  const statId = Number(stat?.parameter_id ?? stat?.param_id ?? stat?.id);
  const normalizedId = Number.isFinite(statId) && statId > 0 ? statId : null;
  const statName = stat?.parameter_name || stat?.name || stat?.title || "";

  return {
    id: normalizedId,
    name: statName,
  };
};

export const collectOverviewStatParameterIds = (overviewStats) => {
  const parameterIds = new Set();

  [
    overviewStats?.main_stats,
    overviewStats?.in_attack,
    overviewStats?.in_defence,
  ].forEach((statGroup) => {
    (Array.isArray(statGroup) ? statGroup : []).forEach((stat) => {
      const { id } = getStatIdentity(stat);
      if (id !== null) {
        parameterIds.add(id);
      }
    });
  });

  return Array.from(parameterIds);
};

export const mergeStatsIntoTemplate = (
  templateStats = [],
  aggregatedStats = []
) => {
  if (!Array.isArray(templateStats) || templateStats.length === 0) {
    return Array.isArray(aggregatedStats) ? aggregatedStats : [];
  }

  const aggregatedMap = new Map();

  (aggregatedStats || []).forEach((stat) => {
    const { id, name } = getStatIdentity(stat);

    if (id !== null) {
      aggregatedMap.set(`id:${id}`, stat);
    }

    if (name) {
      aggregatedMap.set(`name:${name}`, stat);
    }
  });

  return templateStats.map((templateStat) => {
    const { id, name } = getStatIdentity(templateStat);
    const matchedStat =
      (id !== null && aggregatedMap.get(`id:${id}`)) ||
      (name && aggregatedMap.get(`name:${name}`)) ||
      null;

    const nextCount =
      matchedStat !== null
        ? Number(matchedStat?.count ?? matchedStat?.value ?? 0)
        : Number(templateStat?.count ?? templateStat?.value ?? 0);
    const safeCount = Number.isFinite(nextCount) ? nextCount : 0;

    return {
      ...templateStat,
      ...(matchedStat || {}),
      id: matchedStat?.id ?? templateStat?.id ?? id ?? null,
      param_id:
        matchedStat?.param_id ??
        templateStat?.param_id ??
        templateStat?.parameter_id ??
        templateStat?.id ??
        id ??
        null,
      parameter_id:
        matchedStat?.parameter_id ??
        templateStat?.parameter_id ??
        templateStat?.param_id ??
        templateStat?.id ??
        id ??
        null,
      name:
        templateStat?.name ||
        templateStat?.parameter_name ||
        matchedStat?.name ||
        matchedStat?.parameter_name ||
        "",
      parameter_name:
        templateStat?.parameter_name ||
        templateStat?.name ||
        matchedStat?.parameter_name ||
        matchedStat?.name ||
        "",
      count: safeCount,
      value: safeCount,
      event_count:
        matchedStat !== null
          ? (matchedStat?.event_count ?? matchedStat?.unique_markers_count ?? matchedStat?.unique_count ?? templateStat?.event_count ?? templateStat?.unique_markers_count ?? templateStat?.unique_count)
          : (templateStat?.event_count ?? templateStat?.unique_markers_count ?? templateStat?.unique_count),
      unique_count:
        matchedStat !== null
          ? (matchedStat?.event_count ?? matchedStat?.unique_count ?? matchedStat?.unique_markers_count ?? templateStat?.event_count ?? templateStat?.unique_count ?? templateStat?.unique_markers_count)
          : (templateStat?.event_count ?? templateStat?.unique_count ?? templateStat?.unique_markers_count),
      unique_markers_count:
        matchedStat !== null
          ? (matchedStat?.event_count ?? matchedStat?.unique_markers_count ?? matchedStat?.unique_count ?? templateStat?.event_count ?? templateStat?.unique_markers_count ?? templateStat?.unique_count)
          : (templateStat?.event_count ?? templateStat?.unique_markers_count ?? templateStat?.unique_count),
      markers: matchedStat?.markers?.length ? matchedStat.markers : (templateStat?.markers || []),
    };
  });
};

const GOALS_CONCEDED_PARAM_ID = 2;

export const buildMatchBackedOverview = ({
  seasonLeaders,
  ownStats = [],
  opponentStats = [],
}) => {
  // Goals Conceded (param_id=2) is stored in our own team's stats row, not the
  // opponent's. opponentStats.param_id=2 = opponent's goals conceded = our goals
  // scored, which is wrong for the defence display. Pull param_id=2 from ownStats
  // and everything else from opponentStats when updating in_defence.
  const getParamId = (s) => s.parameter_id ?? s.param_id ?? s.id ?? null;
  const defenceStats = [
    ...ownStats.filter((s) => getParamId(s) === GOALS_CONCEDED_PARAM_ID),
    ...opponentStats.filter((s) => getParamId(s) !== GOALS_CONCEDED_PARAM_ID),
  ];

  return {
    ...(seasonLeaders || {}),
    main_stats: mergeStatsIntoTemplate(seasonLeaders?.main_stats, ownStats),
    in_attack: mergeStatsIntoTemplate(seasonLeaders?.in_attack, ownStats),
    in_defence: mergeStatsIntoTemplate(seasonLeaders?.in_defence, defenceStats),
    team_leaders: seasonLeaders?.team_leaders || [],
  };
};
